import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

/**
 * CSRF double-submit cookie helper.
 *
 * The backend mounts /api/v1/csrf which issues a `csrf-token` cookie.
 * This service:
 *   1. bootstraps the cookie on app load (call `bootstrap()` from AppComponent),
 *   2. reads the token out of `document.cookie` for the JWT interceptor
 *      to attach as `X-CSRF-Token` on state-changing requests.
 */
@Injectable({ providedIn: 'root' })
export class CsrfService {
  private static readonly COOKIE_NAME = 'csrf-token';

  constructor(private http: HttpClient) {}

  /**
   * Returns the CSRF token URL. The frontend's baseUrl points to a
   * vendor-scoped prefix (`/api/v1/001/`); the CSRF endpoint lives one
   * level up at `/api/v1/csrf`, so strip the trailing segment.
   */
  private endpoint(): string {
    return environment.baseUrl.replace(/\/\d+\/?$/, '/') + 'csrf';
  }

  /**
   * Fetches a fresh CSRF cookie. Safe to call on every app load — the
   * backend preserves an existing cookie, so this is a no-op when the
   * client already has a valid one.
   */
  bootstrap(): Observable<unknown> {
    return this.http
      .get(this.endpoint(), { withCredentials: true })
      .pipe(
        catchError(() => of(null)),
        tap(() => {}),
      );
  }

  /** Reads the CSRF token from document.cookie. Returns null if absent. */
  getToken(): string | null {
    if (typeof document === 'undefined' || !document.cookie) return null;
    const parts = document.cookie.split(';');
    for (const part of parts) {
      const [rawName, ...rawValue] = part.trim().split('=');
      if (rawName === CsrfService.COOKIE_NAME) {
        return decodeURIComponent(rawValue.join('='));
      }
    }
    return null;
  }
}
